<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <template v-else-if="supplier">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ supplier.code }}
            </h3>
            <div>{{ supplier.name || '—' }}</div>
          </div>
          <b-button
            v-if="hasPermission('edit suppliers')"
            size="sm"
            variant="outline-primary"
            class="rounded-pill"
            :to="{ name: 'ledger-suppliers-edit', params: { id: supplier.id } }"
          >
            {{ $t('ledger_sup_edit') }}
          </b-button>
        </div>
        <div class="ledger-sup-kpis mt-1">
          <div>
            <span class="text-muted">{{ $t('ledger_sup_debit') }}</span>
            <strong class="ledger-num d-block">{{ money(supplier.debit) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_sup_credit') }}</span>
            <strong class="ledger-num d-block">{{ money(supplier.credit) }}</strong>
          </div>
        </div>
        <p class="text-muted small mb-0 mt-1">
          {{ $t('ledger_sup_no_journal') }}
        </p>
      </b-card>

      <b-card class="rounded-lg">
        <b-row>
          <b-col
            v-for="field in detailFields"
            :key="field.label"
            :cols="field.cols || 6"
            :md="field.md || 3"
            class="mb-1"
          >
            <span class="text-muted d-block">{{ field.label }}</span>
            <span :class="{ 'ledger-num': field.numeric }">{{ field.value }}</span>
          </b-col>
        </b-row>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BButton, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getSupplier } from '@/api/accounting/suppliers'
import {
  oneFrom, formatLedgerMoney, apiErrorMessage, glAccountText,
} from '@/utils/ledger'

export default {
  name: 'SupplierShow',
  components: {
    BCard, BButton, BRow, BCol, BSpinner,
  },
  data() {
    return {
      loading: false,
      supplier: null,
    }
  },
  computed: {
    detailFields() {
      const supplier = this.supplier || {}
      return [
        { label: this.$t('ledger_sup_code'), value: this.shown(supplier.code) },
        { label: this.$t('ledger_sup_name'), value: this.shown(supplier.name) },
        { label: this.$t('ledger_gl_account'), value: glAccountText(supplier) || '—' },
        { label: this.$t('ledger_sup_phone'), value: this.shown(supplier.phone) },
        { label: this.$t('ledger_sup_city'), value: this.shown(supplier.city) },
        { label: this.$t('ledger_sup_account_code'), value: this.shown(supplier.account_code) },
        { label: this.$t('ledger_sup_in_use'), value: supplier.in_use ? this.$t('ledger_yes') : this.$t('ledger_no') },
        { label: this.$t('ledger_sup_credit_limit'), value: this.money(supplier.credit_limit), numeric: true },
        { label: this.$t('ledger_sup_tax_number'), value: this.shown(supplier.tax_number) },
        { label: this.$t('ledger_sup_nationality'), value: this.shown(supplier.nationality) },
        { label: this.$t('ledger_sup_email'), value: this.shown(supplier.email) },
        { label: this.$t('ledger_sup_contact_name'), value: this.shown(supplier.contact_name) },
        { label: this.$t('ledger_sup_region'), value: this.shown(supplier.region) },
        { label: this.$t('ledger_sup_country'), value: this.shown(supplier.country) },
        { label: this.$t('ledger_sup_postal_code'), value: this.shown(supplier.postal_code) },
        { label: this.$t('ledger_sup_po_box'), value: this.shown(supplier.po_box) },
        { label: this.$t('ledger_sup_fax'), value: this.shown(supplier.fax) },
        { label: this.$t('ledger_sup_representative'), value: this.shown(supplier.representative) },
        { label: this.$t('ledger_sup_bank_account'), value: this.shown(supplier.bank_account) },
        { label: this.$t('ledger_sup_address'), value: this.shown(supplier.address), cols: 12, md: 12 },
        { label: this.$t('ledger_sup_notes'), value: this.shown(supplier.notes), cols: 12, md: 12 },
        { label: this.$t('ledger_sup_entered_by'), value: this.personName(supplier.entered_by) },
        { label: this.$t('ledger_sup_entered_at'), value: this.dateText(supplier.entered_at) },
      ]
    },
  },
  watch: {
    '$route.params.id': 'load',
  },
  mounted() {
    this.load()
  },
  methods: {
    shown(value) {
      if (value === null || value === undefined || value === '') return '—'
      return value
    },
    money(value) {
      if (value === null || value === undefined || value === '') return '—'
      return formatLedgerMoney(value, this.$i18n.locale)
    },
    personName(value) {
      if (!value) return '—'
      if (typeof value === 'string' || typeof value === 'number') return String(value)
      return value.name || value.name_ar || value.full_name || value.email || (value.id ? `#${value.id}` : '—')
    },
    dateText(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').replace(/\.\d+Z?$/, '').slice(0, 19)
    },
    async load() {
      this.loading = true
      try {
        this.supplier = oneFrom(await getSupplier(this.$route.params.id))
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: error?.response?.status === 422 ? 'warning' : 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-sup-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
</style>
